import {
  type ResidenceAndRoomsType,
  type ResidenceType,
  type Room,
} from '../common/residence'
import { type Truck } from '../common/truck'
import {
  type MoveContentPayload,
  type PreliminaryMoveContentPayload,
  type EstimatedRequirements,
  type SupportedValues,
  type EstimateNbMenOptions,
} from './mw.types'

const mw = (window as any).mw

/**
 * estimate the average nb of boxes based on residence type
 */
export const estimateNbBoxes = (residenceType?: string): number | undefined => {
  return mw?.estimateNbBoxes(residenceType)
}

/**
 * estimate rooms based on residence type
 */
export const estimateRooms = (residenceType?: string): Record<Room, number> | undefined => {
  return mw?.estimateRooms(residenceType)
}

/**
 * estimate the volume of a move
 */
export const estimateVolume = (payload: MoveContentPayload): number => {
  return mw?.estimateVolume(payload) ?? 0
}

/**
 * estimate the volume of the move at early stage
 * where we don't have all the details about the residence type, rooms and special items.
 */
export const estimatePreliminaryVolume = (payload: PreliminaryMoveContentPayload): number => {
  return mw?.estimatePreliminaryVolume(payload) ?? 0
}

/**
 * estimate the requirements of a move based on the volume
 */
export const estimateRequirements = (volume?: number): EstimatedRequirements => {
  return mw?.estimateRequirements(volume) ?? {
    volumeCubicFeet: 0,
    weightPounds: 0,
    nbMen: 0,
    estimatedMovingLabourTime: 0,
    estimatedPackingLabourTime: 0,
  }
}

/**
 * estimate weight from volume based on the volume
 */
export const estimateWeight = (volume?: number): number => {
  return mw?.estimateWeight(volume) ?? 0
}

/**
 * estimate nb trucks needed based on the volume
 */
export const estimateNbTrucks = (volume?: number): Record<Truck, number> => {
  return mw?.estimateNbTrucks(volume) ?? {}
}

/**
 * estimante nb of men needed based on the volume
 */
export const estimateNbMen = (volume?: number, options?: EstimateNbMenOptions): number => {
  return mw?.estimateNbMen(volume, options) ?? 0
}

/**
 * estimate the requirements of a move based on the volume
 */
export const estimateMovingLabourTime = (volume?: number, nbMen?: number): number => {
  const value = mw?.estimateMovingLabourTime(volume, nbMen) ?? 0
  return value === Infinity ? 0 : value
}

/**
 * estimate the requirements of a move based on the volume
 */
export const estimatePackingLabourTime = (volume?: number, nbMen?: number): number => {
  const value = mw?.estimatePackingLabourTime(volume, nbMen) ?? 0
  return value === Infinity ? 0 : value
}

/**
 * exposes values supported by the estimator
 */
export const getSupportedValues = (): SupportedValues => {
  return mw?.getSupportedValues() ?? {
    boxesQuantities: [],
    furnitureRatio: [],
  }
}

/**
 * exposes values supported by the estimator
 */
export const getResidenceGeneralType = (residenceType?: ResidenceType | ResidenceAndRoomsType): ResidenceType | undefined => {
  return mw?.getResidenceGeneralType(residenceType)
}
